import { useViewport, ViewportPortal } from "@xyflow/react";
import { CANVAS_FLOATING_TOOLBAR_Z_INDEX } from "../../constants/canvasFloatingToolbar.constants";

interface CanvasFloatingToolbarPortalProps {
  positionAbsoluteX: number;
  positionAbsoluteY: number;
  width: number;
  /**
   * Base upward offset in screen px (applied before zoom division).
   *
   * For nodes WITHOUT a title pill: set to the desired constant gap above the node
   * top (e.g. 64 for Label/Entity).
   *
   * For nodes WITH a title pill: set to (top_bridge + toolbar_height + gap_above_pill),
   * where top_bridge = h-2 = 8px, toolbar_height ≈ 42px. E.g. for an 8px gap:
   *   topOffset = 8 + 42 + 8 = 58
   *
   * - Label / Entity: 64  (no title pill — constant screen offset above node top)
   * - Note / Section:  58  (pill at -top-9; h-2 bridge + ~42px toolbar + 8px gap)
   */
  topOffset?: number;
  /**
   * The CSS `top` offset (in px, positive) that places the title pill above the node.
   * Must match the Tailwind `-top-N` class on the pill wrapper (e.g. `-top-9` = 36 px).
   *
   * When non-zero, the portal anchors relative to the pill top rather than the node
   * body, giving a constant screen-space gap at every zoom level:
   *   effectiveOffset = topOffset + titlePillCssOffset × zoom
   *
   * - Note / Section: 36  (pill at `-top-9` = 36 px above the node body)
   * - Label / Entity:  0  (default — no pill, offset is zoom-invariant)
   */
  titlePillHeight?: number;
  onPointerDown?: (e: React.PointerEvent) => void;
  children: React.ReactNode;
}

/**
 * Shared ViewportPortal wrapper for all canvas floating toolbars.
 *
 * Handles:
 *   - Zoom-invariant sizing via scale(1/zoom)
 *   - Correct absolute flow-space positioning
 *   - Minimal pointer-event bridge (h-2) so popover interactions don't
 *     accidentally deselect the node
 *   - No hover callbacks needed — toolbar visibility is select-gated
 */
export function CanvasFloatingToolbarPortal({
  positionAbsoluteX,
  positionAbsoluteY,
  width,
  topOffset = 64,
  titlePillHeight = 0, // 36 for Note/Section (matches -top-9 = 36px)
  onPointerDown,
  children,
}: CanvasFloatingToolbarPortalProps) {
  const viewport = useViewport();

  // The title pill sits at `-top-N` (= titlePillHeight px) above the node body
  // in flow space. Its screen-space position therefore scales with zoom.
  //
  // To keep a constant screen-space gap between the toolbar bottom and the pill
  // top at EVERY zoom level we anchor relative to the pill top, not the node body:
  //
  //   effectiveOffset = topOffset + titlePillHeight × zoom
  //
  // Proof (screen space, ignoring viewport pan which cancels):
  //   toolbar_bottom = positionAbsoluteY × zoom − effectiveOffset + toolbar_height
  //   pill_top       = positionAbsoluteY × zoom − titlePillHeight × zoom
  //   gap            = pill_top − toolbar_bottom
  //                  = topOffset − toolbar_height   ← constant ✓
  //
  // For nodes without a pill (titlePillHeight = 0):
  //   effectiveOffset = topOffset  (constant, zoom-invariant) ✓
  const effectiveOffset = topOffset + titlePillHeight * viewport.zoom;

  return (
    <ViewportPortal>
      <div
        className="canvas-floating-toolbar-portal"
        style={{
          position: "absolute",
          left: positionAbsoluteX + width / 2,
          top: positionAbsoluteY - effectiveOffset / viewport.zoom,
          transform: `translateX(-50%) scale(${1 / viewport.zoom})`,
          transformOrigin: "top center",
          pointerEvents: "auto",
          zIndex: CANVAS_FLOATING_TOOLBAR_Z_INDEX,
        }}
        onPointerDown={(e) => {
          // Stop propagation so React Flow doesn't process this as a canvas
          // click and deselect the node.
          e.stopPropagation();
          onPointerDown?.(e);
        }}
      >
        {/* Minimal bridge above — fills the visible gap between toolbar bottom
            and node (title pill / body). Pointer-events are captured here so
            popovers can be interacted with without losing selection. */}
        <div className="h-2 w-full" />
        {children}
        {/* Bridge below keeps pointer-events within the portal area */}
        <div className="h-2 w-full" />
      </div>
    </ViewportPortal>
  );
}
